import { create } from "zustand";
import { fetchApi } from "@/lib/api";
import { captureException } from "@/lib/analytics";

export interface ModelDefinition {
  model: string;
  name: string;
  description: string;
  isReasoningModel: boolean;
  provider: "anthropic" | "openai" | "xai" | "google";
}

interface LLMState {
  models: ModelDefinition[];
  isLoadingModels: boolean;
  modelsError: string | null;
  hasLoadedModels: boolean;
}

interface LLMActions {
  fetchModels: () => Promise<void>;
  reset: () => void;
}

type LLMStore = LLMState & LLMActions;

const initialState: LLMState = {
  models: [],
  isLoadingModels: false,
  modelsError: null,
  hasLoadedModels: false,
};

export const useLLMStore = create<LLMStore>((set, get) => ({
  ...initialState,

  fetchModels: async () => {
    // If already loaded or currently loading, don't fetch again
    const { hasLoadedModels, isLoadingModels } = get();
    if (hasLoadedModels || isLoadingModels) {
      return;
    }

    set({ isLoadingModels: true, modelsError: null });

    try {
      const response = await fetchApi("llm/models", "GET");
      if (response.ok && response.data) {
        const data = response.data as { models: ModelDefinition[] };
        set({
          models: data.models,
          hasLoadedModels: true,
          isLoadingModels: false,
          modelsError: null,
        });
      } else {
        throw new Error("Failed to fetch models");
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to fetch models";
      set({
        modelsError: errorMessage,
        isLoadingModels: false,
      });
      captureException(new Error(errorMessage));
    }
  },

  reset: () => {
    set(initialState);
  },
}));

// Helper selectors
export const useModels = () => {
  const models = useLLMStore((state) => state.models);
  const isLoading = useLLMStore((state) => state.isLoadingModels);
  const error = useLLMStore((state) => state.modelsError);
  const fetchModels = useLLMStore((state) => state.fetchModels);

  return { models, isLoading, error, fetchModels };
};

export const useReasoningModels = () => {
  const models = useLLMStore((state) => state.models);
  return models.filter((model) => model.isReasoningModel);
};

export const useNonReasoningModels = () => {
  const models = useLLMStore((state) => state.models);
  return models.filter((model) => !model.isReasoningModel);
};

export const getModelByEnum = (modelEnum: string) => {
  const models = useLLMStore.getState().models;
  return models.find((model) => model.model === modelEnum);
};
